import { ISourceDataConfigurationFormField, ISourceDataConfigurationFormFieldValue } from "@/api/dtos";
import { createValues, IConfigurationFormEditUpdateItem, ISelectionOption, IUpdateMapping, updateBySelection, updateValue, updateValues } from '.';

export const ConfigurationUpdateFieldMapping: IUpdateMapping = {
    createOptions:  (source, target) => {
        let options: ISelectionOption[] = [
            {
                name: 'options',
                label: 'Options',
                options: [
                    {
                        name: 'overrideName',
                        label: 'Override Name',
                        update: (source, target, sourceValue, targetValue, newValue) => {
                            (newValue as ISourceDataConfigurationFormField).name = (sourceValue as ISourceDataConfigurationFormField).name;
                        }
                    },
                    {
                        name: 'priority',
                        label: 'Priority',
                        update: (source, target, sourceValue, targetValue, newValue) => {
                            (newValue as ISourceDataConfigurationFormField).priority = (sourceValue as ISourceDataConfigurationFormField).priority;
                        }
                    },
                    {
                        name: 'excludeBlank',
                        label: 'Hide Field When No Data Provided',
                        update: (source, target, sourceValue, targetValue, newValue) => {
                            (newValue as ISourceDataConfigurationFormField).excludeBlank = (sourceValue as ISourceDataConfigurationFormField).excludeBlank;
                        }
                    },
                    {
                        name: 'sourceDataVerification',
                        label: 'Source Data Verification',
                        options: [
                            {
                                name: 'allowSourceDataVerification',
                                label: 'Allow Source Data Verification',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).allowSourceDataVerification = (sourceValue as ISourceDataConfigurationFormField).allowSourceDataVerification;
                                }
                            },
                            {
                                name: 'allowReview',
                                label: 'Allow Review',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).allowReview = (sourceValue as ISourceDataConfigurationFormField).allowReview;
                                }
                            },
                            {
                                name: 'allowVerify',
                                label: 'Allow Verification',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).allowVerify = (sourceValue as ISourceDataConfigurationFormField).allowVerify;
                                }
                            }
                        ]
                    },
                    {
                        name: 'findings',
                        label: 'Findings',
                        options: [
                            {
                                name: 'allowFindings',
                                label: 'Allow Findings',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).allowFindings = (sourceValue as ISourceDataConfigurationFormField).allowFindings;
                                }
                            }
                        ]
                    },
                    {
                        name: 'alerts',
                        label: 'Alerts',
                        options: [
                            {
                                name: 'information',
                                label: 'Information',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).alertInformation = (sourceValue as ISourceDataConfigurationFormField).alertInformation;
                                }
                            },
                            {
                                name: 'warning',
                                label: 'Warning',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).alertWarning = (sourceValue as ISourceDataConfigurationFormField).alertWarning;
                                }
                            },
                            {
                                name: 'error',
                                label: 'Error',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).alertError = (sourceValue as ISourceDataConfigurationFormField).alertError;
                                }
                            },
                            {
                                name: 'success',
                                label: 'Success',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).alertSuccess = (sourceValue as ISourceDataConfigurationFormField).alertSuccess;
                                }
                            }
                        ]
                    },
                    {
                        name: 'auditHistory',
                        label: 'Audit History',
                        options: [
                            {
                                name: 'showAudit',
                                label: 'Show Audit History',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).showAudit = (sourceValue as ISourceDataConfigurationFormField).showAudit;
                                }
                            }
                        ]
                    },
                    {
                        name: 'discrepancyNotes',
                        label: 'Discrepancy Notes',
                        options: [
                            {
                                name: 'showNotes',
                                label: 'Show Discrepancy Notes',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).showNotes = (sourceValue as ISourceDataConfigurationFormField).showNotes;
                                }
                            },
                            {
                                name: 'types',
                                label: 'Types',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).noteTypes = (sourceValue as ISourceDataConfigurationFormField).noteTypes;
                                }
                            },
                            {
                                name: 'statuses',
                                label: 'Statuses',
                                update: (source, target, sourceValue, targetValue, newValue) => {
                                    (newValue as ISourceDataConfigurationFormField).noteStatuses = (sourceValue as ISourceDataConfigurationFormField).noteStatuses;
                                }
                            }
                        ]
                    }
                ]
            }
        ];

        if (source.formFieldMetadata?.values && source.formFieldMetadata.values.length !== 0) {
            options.push({
                name: 'values',
                label: 'Values',
                options: source.formFieldMetadata.values.map(x => ({
                    name: x.id!,
                    label: `${x.id} - ${x.name}`,
                    update: (source, target, sourceValue, targetValue, newValue) => {
                        const typedNewValue = (newValue as ISourceDataConfigurationFormField);
                        const typedTargetValue = (targetValue as ISourceDataConfigurationFormField);
                        const typedSourceValue = (sourceValue as ISourceDataConfigurationFormField);

                        if (!typedNewValue.linked) {
                            createValues(typedSourceValue, typedTargetValue, typedNewValue, target.formFieldMetadata);
                        }

                        updateValue(typedSourceValue, typedTargetValue, typedNewValue, x.id!);
                    }
                }))
            });
        }

        return options;
    },
    update: (source, target, sourceValue, targetValue, options, selection) => {
        const newValue = { 
            ...targetValue,
            linked: sourceValue.linked
        };

        updateBySelection(source, target, sourceValue, targetValue, newValue, options, selection);

        return newValue;
    }
}